import React from "react";
import { X, Heart, Gem, BadgeCheck, Shield, Printer, ChevronLeft, ChevronRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useRates } from "@/context/RatesContext";
import { useWishlist } from "@/context/WishlistContext";
import { computePrice, fmtINR } from "@/lib/pricing";
import { fileUrl } from "@/lib/api";

export default function ProductModal({ item, onClose }) {
  const { rates, gstOn, setGstOn } = useRates();
  const { has, toggle } = useWishlist();
  const [activeImg, setActiveImg] = React.useState(0);
  if (!item) return null;
  const price = computePrice(item, rates, gstOn);
  const isDiamond = item.diamond_weight > 0;
  const active = has(item.tag_no);
  const isRingstudio = item.collection === "ringstudio";
  const gallery = (item.images || []).filter(Boolean);
  if (gallery.length === 0 && item.image_path) gallery.push(item.image_path);
  const currentImg = fileUrl(gallery[activeImg]);

  const doPrint = () => {
    window.open(`/print-tag/${encodeURIComponent(item.tag_no)}`, "_blank");
  };

  const nextImg = (e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % Math.max(1, gallery.length)); };
  const prevImg = (e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + Math.max(1, gallery.length)) % Math.max(1, gallery.length)); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/80 backdrop-blur-md p-0 md:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="product-modal"
    >
      <div
        className="bg-white border-0 md:border md:border-gold/30 w-full max-w-5xl md:my-6 relative grid grid-cols-1 md:grid-cols-2 gap-0 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ color: '#0B1A33' }}
      >
        <button onClick={onClose} className="absolute top-3 right-3 z-30 p-2 bg-navy-900 hover:bg-navy-800 border border-gold/30" data-testid="modal-close-button">
          <X className="w-4 h-4 text-ivory" />
        </button>

        <div className="relative">
          <div className="relative overflow-hidden" style={{ aspectRatio: "1 / 1", background: "linear-gradient(135deg,#FFF7EC,#F5E6CC)" }}>
            {currentImg ? (
              <img src={currentImg} alt={item.item_name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Gem className="w-32 h-32 text-gold/30" strokeWidth={0.5} />
              </div>
            )}
            {gallery.length > 1 && (
              <>
                <button onClick={prevImg} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-navy-900/85 border border-gold/30 hover:border-gold"><ChevronLeft className="w-5 h-5 text-ivory" /></button>
                <button onClick={nextImg} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-navy-900/85 border border-gold/30 hover:border-gold"><ChevronRight className="w-5 h-5 text-ivory" /></button>
              </>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto bg-ivory-warm" data-testid="modal-thumbnails">
              {gallery.map((g, i) => (
                <button key={`${g}-${i}`} onClick={() => setActiveImg(i)} className={`flex-shrink-0 w-16 h-16 border ${i === activeImg ? "border-gold" : "border-gold/20"} overflow-hidden`}>
                  <img src={fileUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 md:p-10 space-y-5 md:max-h-[85vh] md:overflow-y-auto" style={{ color: '#0B1A33' }}>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="overline" style={{ color: '#A88934' }}>{item.tag_no}</span>
              <span className="overline" style={{ color: 'rgba(11,26,51,0.55)' }}>• {item.karat}</span>
              {isRingstudio && <span className="overline border px-2 py-0.5" style={{ color: '#A88934', borderColor: 'rgba(168,137,52,0.5)' }}>Ringstudio</span>}
              {isDiamond && !isRingstudio && <span className="overline border px-2 py-0.5" style={{ color: '#A88934', borderColor: 'rgba(168,137,52,0.5)' }}>Diamond</span>}
            </div>
            <h2 className="font-display text-2xl md:text-4xl leading-tight" style={{ color: '#1a0f00', fontWeight: 600 }}>{item.item_name}</h2>
            {item.metal_colour && <div className="overline mt-2" style={{ color: 'rgba(11,26,51,0.6)' }}>{item.metal_colour}</div>}
            {!item.metal_colour && <div className="overline mt-2" style={{ color: 'rgba(11,26,51,0.6)' }}>{item.category}</div>}
            {item.description && <p className="text-sm mt-4 leading-relaxed" style={{ color: 'rgba(11,26,51,0.78)' }}>{item.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm border-t pt-6" style={{ borderColor: 'rgba(168,137,52,0.25)' }}>
            {item.gross_weight > 0 && <Spec label="Gross Weight" value={`${item.gross_weight.toFixed(3)} g`} />}
            {item.gold_weight > 0 && <Spec label="Gold Weight" value={`${item.gold_weight.toFixed(3)} g`} />}
            {item.net_weight > 0 && <Spec label="Net Weight" value={`${item.net_weight.toFixed(3)} g`} />}
            {isDiamond && <Spec label="Diamond Weight" value={`${item.diamond_weight.toFixed(3)} ct`} />}
            {item.no_of_diamonds > 0 && <Spec label="Stones" value={item.no_of_diamonds} />}
            {item.shape && <Spec label="Shape" value={item.shape} />}
            {item.size && <Spec label="Size" value={item.size} />}
            {item.pieces > 0 && <Spec label="Pieces" value={item.pieces} />}
            {item.hallmark && <Spec label="Hallmark" value={item.hallmark} icon={<BadgeCheck className="w-3 h-3" />} />}
          </div>

          {isDiamond && (item.colour || item.clarity || item.certification) && (
            <div className="border-t pt-6" style={{ borderColor: 'rgba(168,137,52,0.25)' }}>
              <div className="overline mb-3 flex items-center gap-2" style={{ color: '#A88934' }}><Gem className="w-3 h-3" /> Diamond Details</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {item.colour && <Spec label="Colour" value={item.colour} />}
                {item.clarity && <Spec label="Clarity" value={item.clarity} />}
                {item.certification && <Spec label="Certification" value={item.certification} icon={<Shield className="w-3 h-3" />} />}
              </div>
            </div>
          )}

          <div className="border-t pt-6 space-y-3" data-testid="price-breakdown" style={{ borderColor: 'rgba(168,137,52,0.25)' }}>
            <div className="overline mb-2" style={{ color: '#A88934' }}>{price.mrpMode ? "MRP" : "Live Price Breakdown"}</div>
            {!price.mrpMode && (
              <>
                <Row label={`Gold Value (${item.karat} @ ${fmtINR(price.karatRate)}/g)`} value={fmtINR(price.goldValue)} />
                <Row label="Making Charges" value={fmtINR(price.makingCharges)} />
                {isDiamond && <Row label={`Diamond (${item.diamond_weight.toFixed(3)}ct × ₹60,000)`} value={fmtINR(price.diamondValue)} />}
                <div className="border-t pt-3 flex justify-between items-center" style={{ borderColor: 'rgba(168,137,52,0.2)' }}>
                  <span style={{ color: 'rgba(11,26,51,0.7)' }}>Subtotal</span>
                  <span className="font-medium" style={{ color: '#0B1A33' }}>{fmtINR(price.subtotal)}</span>
                </div>
              </>
            )}
            {price.mrpMode && (
              <div className="flex justify-between items-center">
                <span style={{ color: 'rgba(11,26,51,0.7)' }}>Listed price</span>
                <span className="font-medium" style={{ color: '#0B1A33' }}>{fmtINR(price.subtotal)}</span>
              </div>
            )}
            <div className="flex justify-between items-center gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={gstOn} onCheckedChange={setGstOn} data-testid="gst-toggle" />
                <span className="text-sm" style={{ color: 'rgba(11,26,51,0.7)' }}>GST 3%</span>
              </label>
              <span className="text-sm" style={{ color: gstOn ? '#0B1A33' : 'rgba(11,26,51,0.35)' }}>{fmtINR(price.gst)}</span>
            </div>
            <div className="border-t pt-4 flex justify-between items-baseline" style={{ borderColor: 'rgba(168,137,52,0.4)' }}>
              <span className="overline" style={{ color: '#A88934' }}>Total</span>
              <span className="font-display text-3xl" data-testid="final-price" style={{ color: '#0B1A33', fontWeight: 600 }}>{fmtINR(price.final)}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={() => toggle(item)}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 md:py-4 border tracking-widest text-sm uppercase transition-colors"
              style={active ? { background: '#C9A84C', color: '#0B1A33', borderColor: '#C9A84C' } : { borderColor: 'rgba(201,168,76,0.5)', color: '#A88934' }}
              data-testid="modal-wishlist-button"
            >
              <Heart className={`w-4 h-4 ${active ? "fill-current" : ""}`} />
              {active ? "In Wishlist" : "Add to Wishlist"}
            </button>
            <button onClick={doPrint} className="px-5 py-3.5 md:py-4 border tracking-widest text-sm uppercase flex items-center justify-center gap-2 hover:border-gold hover:text-gold-dark transition-colors" style={{ borderColor: 'rgba(11,26,51,0.2)', color: 'rgba(11,26,51,0.75)' }} data-testid="modal-print-button">
              <Printer className="w-4 h-4" /> Print Tag
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Spec = ({ label, value, icon }) => (
  <div className="flex flex-col gap-1">
    <span className="overline" style={{ color: 'rgba(11,26,51,0.55)' }}>{label}</span>
    <span className="flex items-center gap-1.5" style={{ color: '#1a0f00', fontWeight: 500 }}>{icon}{value}</span>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span style={{ color: 'rgba(11,26,51,0.65)' }}>{label}</span>
    <span style={{ color: '#0B1A33', fontWeight: 500 }}>{value}</span>
  </div>
);
